import { HttpClient, HttpParams } from '@angular/common/http';
import { EventEmitter, Injectable, Output } from '@angular/core';

import { catchError, Observable } from 'rxjs';

import { ErrorHandlerService } from './error-handler.service';
import { environment } from 'src/environments/environment.development';
import { TestPayload } from 'src/app/shared/models/test-payload.model';
import { SuccessResponse } from 'src/app/shared/models/success-response.model';
import { Candidate } from 'src/app/shared/models/candidate.model';

@Injectable({
  providedIn: 'root',
})
export class TestService {
  @Output() addQuestionToTestPool$ = new EventEmitter<any>();
  @Output() deleteQuestionfromTestPool$ = new EventEmitter<any>();

  constructor(
    private http: HttpClient,
    private errorHandlerService: ErrorHandlerService
  ) { }

  addTest(testData: TestPayload): Observable<any> {
    return this.http
      .post<any>(`${environment.API_URL}/wgassessmentportal/test`, testData)
      .pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  getTestList(year: number): Observable<any> {
    return this.http
      .get<any>(
        `${environment.API_URL}/wgassessmentportal/test/list?year=${year}`
      )
      .pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  getTestbyId(testId: string): Observable<any> {
    return this.http
      .get<any>(`${environment.API_URL}/wgassessmentportal/test/${testId}`)
      .pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  updateTest(testId: string, testPayload: any): Observable<any> {
    return this.http
      .put<any>(`${environment.API_URL}/wgassessmentportal/test/${testId}`, testPayload)
      .pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  scheduleTest(testId: string): Observable<SuccessResponse> {
    return this.http
      .patch<SuccessResponse>(`${environment.API_URL}/wgassessmentportal/test/${testId}`, {})
      .pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  deleteTest(testId: string): Observable<SuccessResponse> {
    return this.http
      .delete<SuccessResponse>(`${environment.API_URL}/wgassessmentportal/test/${testId}`)
      .pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  addCandidate(testId: string, candidates: { "CANDIDATES": Candidate[] }): Observable<SuccessResponse> {
    const url: string = `${environment.API_URL}/wgassessmentportal/test/${testId}/candidate`;
    return this.http
      .post<SuccessResponse>(url, candidates)
      .pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  getCandidateList(testId: string): Observable<SuccessResponse> {
    const url: string = `${environment.API_URL}/wgassessmentportal/test/${testId}/candidate/list`;
    return this.http
      .get<SuccessResponse>(url)
      .pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  allowMachineChange(tokenId: string): Observable<SuccessResponse> {
    const url: string = `${environment.API_URL}/wgassessmentportal/refresh/${tokenId}`;
    return this.http
      .post<SuccessResponse>(url, {})
      .pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  evaluateTest(testId: string, candidateId?: string): Observable<SuccessResponse> {
    let url: string = `${environment.API_URL}/wgassessmentportal/evaluate/${testId}`;
    let params = new HttpParams();
    if (candidateId) {
      params = params.set('candidateId', candidateId);
    }
    return this.http
      .post<SuccessResponse>(url, {}, { params })
      .pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  reevaluateNegativeMarking(testId: string): Observable<SuccessResponse> {
    const url: string = `${environment.API_URL}/wgassessmentportal/evaluate/${testId}`;
    const params = new HttpParams().set('mode', 'negative_marking');
    return this.http
      .post<SuccessResponse>(url, {}, { params })
      .pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  endTest(testId: string): Observable<SuccessResponse> {
    const url: string = `${environment.API_URL}/wgassessmentportal/test/${testId}/end`;
    return this.http
      .patch<SuccessResponse>(url, {})
      .pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  endTestForCandidate(testId: string, candidateId: string): Observable<SuccessResponse> {
    const url: string = `${environment.API_URL}/wgassessmentportal/test/${testId}/end?candidateId=${candidateId}`;
    return this.http
      .patch<SuccessResponse>(url, {})
      .pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  getCandidateTestDetail(testId: string, candidateId: string): Observable<SuccessResponse> {
    const url: string = `${environment.API_URL}/wgassessmentportal/test/${testId}/candidate/${candidateId}`
    return this.http
      .get<SuccessResponse>(url)
      .pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  updateCandidateStatus(testKey: string, candidateIds: string[], status: string): Observable<any> {
    return this.http.put(
      `${environment.API_URL}/wgassessmentportal/test/${testKey}/candidates`,
      {
        candidate_ids: candidateIds,
        status: status.toUpperCase()
      }
    ).pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

}
